'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/store/hooks'
import {
    MdScience,
    MdLocalPharmacy,
    MdMedicalServices,
    MdHealthAndSafety,
    MdBiotech,
    MdArrowForward,
    MdCheckCircle,
    MdCancel,
} from 'react-icons/md'
import { getLabIntegrationStats } from '@/lib/api/health-integrations/lab-integration-api'
import { getTissStats } from '@/lib/api/health-integrations/tiss-api'
import { getRndsStats } from '@/lib/api/health-integrations/rnds-api'
import { getPacsStats } from '@/lib/api/health-integrations/pacs-api'
import { getPharmacyStats } from '@/lib/api/health-integrations/pharmacy-api'

interface IntegrationCard {
    id: string
    name: string
    description: string
    icon: React.ReactNode
    route: string
    color: string
    stats?: {
        active: number
        total: number
    }
}

export default function HealthIntegrationsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [integrations, setIntegrations] = useState<IntegrationCard[]>([
        {
            id: 'lab',
            name: 'Integração Laboratorial',
            description:
                'Receba resultados de exames automaticamente via HL7 v2.x ou FHIR R4',
            icon: <MdScience className="text-4xl" />,
            route: '/health-integrations/lab',
            color: 'from-blue-500 to-blue-600',
        },
        {
            id: 'tiss',
            name: 'TISS/ANS',
            description:
                'Troca de informações com operadoras de planos de saúde',
            icon: <MdMedicalServices className="text-4xl" />,
            route: '/health-integrations/tiss',
            color: 'from-green-500 to-green-600',
        },
        {
            id: 'pharmacy',
            name: 'Receita Eletronica',
            description:
                'Prescrições eletrônicas com Memed, Nexodata e outras plataformas',
            icon: <MdLocalPharmacy className="text-4xl" />,
            route: '/health-integrations/pharmacy',
            color: 'from-purple-500 to-purple-600',
        },
        {
            id: 'pacs',
            name: 'PACS/DICOM',
            description:
                'Visualize e gerencie imagens médicas de servidores PACS',
            icon: <MdBiotech className="text-4xl" />,
            route: '/health-integrations/pacs',
            color: 'from-orange-500 to-orange-600',
        },
        {
            id: 'rnds',
            name: 'RNDS/eSUS',
            description:
                'Envie dados de vacinação e resultados para a Rede Nacional de Dados em Saúde',
            icon: <MdHealthAndSafety className="text-4xl" />,
            route: '/health-integrations/rnds',
            color: 'from-teal-500 to-teal-600',
        },
    ])

    useEffect(() => {
        if (user?.companyId) {
            loadStats()
        }
    }, [user?.companyId])

    const loadStats = async () => {
        setLoading(true)
        try {
            const [labStats, tissStats, pharmacyStats, pacsStats, rndsStats] =
                await Promise.all([
                    getLabIntegrationStats(),
                    getTissStats(),
                    getPharmacyStats(),
                    getPacsStats(),
                    getRndsStats(),
                ])

            setIntegrations(prev =>
                prev.map(integration => {
                    switch (integration.id) {
                        case 'lab':
                            if (labStats.success) {
                                return {
                                    ...integration,
                                    stats: {
                                        active: labStats.result.activeIntegrations,
                                        total: labStats.result.totalIntegrations,
                                    },
                                }
                            }
                            break
                        case 'tiss':
                            if (tissStats.success) {
                                return {
                                    ...integration,
                                    stats: {
                                        active: tissStats.result.totalConfigurations,
                                        total: tissStats.result.totalConfigurations,
                                    },
                                }
                            }
                            break
                        case 'pharmacy':
                            if (pharmacyStats.success) {
                                return {
                                    ...integration,
                                    stats: {
                                        active: pharmacyStats.result.totalIntegrations,
                                        total: pharmacyStats.result.totalIntegrations,
                                    },
                                }
                            }
                            break
                        case 'pacs':
                            if (pacsStats.success) {
                                return {
                                    ...integration,
                                    stats: {
                                        active: pacsStats.result.activeServers,
                                        total: pacsStats.result.totalServers,
                                    },
                                }
                            }
                            break
                        case 'rnds':
                            if (rndsStats.success) {
                                return {
                                    ...integration,
                                    stats: {
                                        active: rndsStats.result.totalConfigurations,
                                        total: rndsStats.result.totalConfigurations,
                                    },
                                }
                            }
                            break
                    }
                    return integration
                })
            )
        } catch (error) {
            console.error('Error loading stats:', error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdHealthAndSafety className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Integrações de Saúde
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Conecte seu sistema a laboratórios, operadoras e redes de saúde
                        </p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                    <p className="text-base-content/60 text-sm">
                        Carregando integrações...
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {integrations.map(integration => (
                        <div
                            key={integration.id}
                            onClick={() => navigate(integration.route)}
                            className="group cursor-pointer bg-base-100 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                        >
                            <div
                                className={`bg-gradient-to-br ${integration.color} p-6 text-white`}
                            >
                                <div className="flex items-center justify-between">
                                    {integration.icon}
                                    {integration.stats && (
                                        <div className="flex items-center gap-1 text-sm bg-white/20 rounded-full px-3 py-1">
                                            {integration.stats.active > 0 ? (
                                                <>
                                                    <MdCheckCircle className="text-lg" />
                                                    <span>
                                                        {integration.stats.active} ativas
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <MdCancel className="text-lg" />
                                                    <span>Nenhuma ativa</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="p-4">
                                <h3 className="text-lg font-bold text-base-content mb-2">
                                    {integration.name}
                                </h3>
                                <p className="text-sm text-base-content/70 mb-4 line-clamp-2">
                                    {integration.description}
                                </p>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-base-content/50">
                                        Clique para gerenciar
                                    </span>
                                    <MdArrowForward className="text-xl text-secondary group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
